/**
 * The gap groups (gaps plan §8).
 *
 * Grouped by kind rather than by document, because the *remedy* is per kind.
 * A list of 22 filings that each need `ingest` mixed in with 108 pages that
 * need `extract --force` is a list nobody can act on.
 *
 * Every group prints the command that would close it whether or not this
 * app is allowed to run anything. That is §9.5's rule -- naming the command
 * beats a spinner that will never resolve -- applied to a whole screen: with
 * actions disabled this is still the most useful page in the app for
 * deciding what to run next.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import { useState } from 'react'
import type { GapGroup, GapItem, GapKind, FailingPage, GapAction } from '@server/sql/gaps.ts'
import type { ActionSpec } from '@server/jobs/actions.ts'
import { Button } from '~/components/ui/primitives.tsx'
import { api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import type { Key } from '~/i18n/en.ts'
import { count } from '~/i18n/format.ts'
import { Badge, Skeleton, type State } from '~/components/ui/primitives.tsx'
import { Command, EmptyState } from '~/components/ui/empty.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'

/**
 * How loudly each kind reads.
 *
 * `failing_check` is deliberately `warn` and not `fail`: 3,332 of the
 * corpus's 3,628 open items are one check that cannot always distinguish a
 * misreading from a layout, and painting that red would assert something the
 * data does not support. `no_ground_truth` is `neutral` -- it is a limit on
 * what can be *said*, not a defect.
 */
const TONE: Record<GapKind, State> = {
  never_ingested: 'fail',
  stalled: 'fail',
  page_never_extracted: 'warn',
  extraction_failed: 'warn',
  failing_check: 'warn',
  image_missing: 'warn',
  concept_unmapped: 'neutral',
  no_ground_truth: 'neutral',
}

/**
 * Written out rather than composed as `gaps.kind.${kind}`, because a template
 * literal has to be cast to be accepted as a key and the cast is exactly what
 * would stop a missing translation being a compile error. `ar.ts` is typed
 * against `en.ts` for the same reason; casting here would open the hole at
 * the other end.
 */
const TITLE: Record<GapKind, Key> = {
  never_ingested: 'gaps.kind.never_ingested',
  stalled: 'gaps.kind.stalled',
  page_never_extracted: 'gaps.kind.page_never_extracted',
  extraction_failed: 'gaps.kind.extraction_failed',
  failing_check: 'gaps.kind.failing_check',
  image_missing: 'gaps.kind.image_missing',
  concept_unmapped: 'gaps.kind.concept_unmapped',
  no_ground_truth: 'gaps.kind.no_ground_truth',
}

const WHY: Record<GapKind, Key> = {
  never_ingested: 'gaps.kind.never_ingestedWhy',
  stalled: 'gaps.kind.stalledWhy',
  page_never_extracted: 'gaps.kind.page_never_extractedWhy',
  extraction_failed: 'gaps.kind.extraction_failedWhy',
  failing_check: 'gaps.kind.failing_checkWhy',
  image_missing: 'gaps.kind.image_missingWhy',
  concept_unmapped: 'gaps.kind.concept_unmappedWhy',
  no_ground_truth: 'gaps.kind.no_ground_truthWhy',
}

/**
 * A gap's remedy, as the API's typed action.
 *
 * The mapping lives here rather than on the server's `GapAction` so the two
 * can differ where they should: a gap describes *what would close it*, and an
 * action describes *what to run*. `reextract_pages` closes three different
 * gap kinds, and `never_ingested` names capture ids that only mean something
 * once a symbol is attached.
 */
function toActionSpec(symbol: string, a: GapAction | null): ActionSpec | null {
  if (!a) return null
  switch (a.kind) {
    case 'ingest':           return { kind: 'ingest_missing', symbol, captureFileIds: a.captureFileIds }
    case 'run_from':         return { kind: 'resume_document', documentId: a.documentId }
    case 'reextract_pages':  return { kind: 'reextract_pages', documentId: a.documentId, pageNos: a.pageNos }
    case 'render':           return { kind: 'render', documentId: a.documentId, tier: a.tier as 'A' | 'A0' | 'B' | 'C' | 'D' }
    case 'renormalize':      return { kind: 'renormalize', symbol }
  }
}

export function GapGroups({ symbol, groups, onRun, actionsEnabled }: {
  symbol: string
  groups: GapGroup[]
  onRun: (spec: ActionSpec, command: string) => void
  actionsEnabled: boolean
}) {
  const present = groups.filter((g) => g.count > 0)
  const { t } = useT()

  if (present.length === 0)
    return <EmptyState title={t('gaps.none')}>{t('gaps.noneWhy')}</EmptyState>

  return (
    <div className="space-y-1">
      {present.map((g) => (
        <Group key={g.kind} symbol={symbol} group={g} onRun={onRun} actionsEnabled={actionsEnabled} />
      ))}
    </div>
  )
}

/**
 * The row's remedy: a button when this build may run things, the command
 * itself when it may not.
 *
 * With actions disabled the button is not merely inert — it is the wrong
 * affordance. What a reader needs then is the exact line to paste into a
 * terminal, per row, because the group's command covers a whole company and
 * the useful unit here is one page. The button still exists in that mode
 * *as well*, because the estimate behind it is a read and is worth having
 * before deciding to go and run something by hand.
 */
function Remedy({ spec, command, onRun, label, enabled }: {
  spec: ActionSpec | null
  command: string | null
  onRun: (s: ActionSpec, c: string) => void
  label: string
  enabled: boolean
}) {
  if (!spec) return null
  return (
    <Button size="sm" variant={enabled ? 'default' : 'ghost'} onClick={() => onRun(spec, command ?? '')}>
      {label}
    </Button>
  )
}

function Group({ symbol, group, onRun, actionsEnabled }: {
  symbol: string; group: GapGroup
  onRun: (s: ActionSpec, c: string) => void; actionsEnabled: boolean
}) {
  const { t, locale } = useT()
  const tone = TONE[group.kind]

  return (
    <Disclosure
      className="rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)] px-3 py-1.5"
      summary={
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[length:var(--text-prose)] font-medium text-[var(--text)]">
            {t(TITLE[group.kind])}
          </span>
          <Badge state={tone} className="tnum">
            {count(group.count, locale)}
          </Badge>
          {group.pages !== undefined && group.pages > 0 && (
            <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
              {count(group.pages, locale)} pp
            </span>
          )}
          {!group.actionable && (
            <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
              {t('gaps.notActionable')}
            </span>
          )}
        </span>
      }
    >
      <div className="pb-2 ps-1 pt-1">
        <p className="max-w-[70ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
          {t(WHY[group.kind])}
        </p>

        {/* The caveat that stops a re-read being read as a fix. */}
        {group.note && (
          <p className="mt-1.5 max-w-[70ch] text-[length:var(--text-data)] text-[var(--warn)]">
            {group.note}
          </p>
        )}

        {group.command && (
          <div className="mt-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
                {t('gaps.command')}
              </span>
              <div className="flex-1" />
              <Remedy
                spec={toActionSpec(symbol, group.action)}
                command={group.command}
                onRun={onRun}
                label={t('action.run')}
                enabled={actionsEnabled}
              />
            </div>
            <Command>{group.command}</Command>
          </div>
        )}

        {group.kind === 'failing_check' ? (
          <ReasonRows symbol={symbol} items={group.items} onRun={onRun} enabled={actionsEnabled} />
        ) : (
          <ItemRows symbol={symbol} items={group.items} onRun={onRun} enabled={actionsEnabled} />
        )}

        {group.truncated && (
          <div className="tnum mt-1.5 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('gaps.showing', {
              n: count(group.items.length, locale),
              total: count(group.count, locale),
            })}
          </div>
        )}
      </div>
    </Disclosure>
  )
}

/** The plain rows: one line each, with the command that closes that one. */
function ItemRows({ symbol, items, onRun, enabled }: {
  symbol: string; items: GapItem[]
  onRun: (s: ActionSpec, c: string) => void; enabled: boolean
}) {
  const { t } = useT()
  if (items.length === 0) return null
  return (
    <ul className="mt-2 space-y-px">
      {items.map((it) => (
        <li
          key={it.id}
          className={cn(
            'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-[var(--radius-control)]',
            'px-1.5 py-1 hover:bg-[var(--bg-sunken)]',
          )}
        >
          <span className="tnum text-[length:var(--text-data)] text-[var(--text)]">{it.label}</span>
          {it.detail && (
            <span className="text-[length:var(--text-micro)] text-[var(--text-muted)]">
              {it.detail}
            </span>
          )}
          <span className="flex-1" />
          {it.documentId !== null && (
            <Link
              href={`/d/${it.documentId}`}
              className="link-inline text-[length:var(--text-micro)] text-[var(--accent)]"
            >
              {it.pageNo === null ? `d${it.documentId}` : `d${it.documentId} p${it.pageNo}`}
            </Link>
          )}
          {/* The row is the unit. Someone checking whether re-extraction
              actually helps wants to try exactly one first, and that has to
              be the cheap path rather than the group. */}
          <Remedy
            spec={toActionSpec(symbol, it.action)}
            command={it.command}
            onRun={onRun}
            label={it.pageNo === null ? t('action.run') : t('action.reread')}
            enabled={enabled}
          />
          {/* With nothing allowed to spawn, the line to paste is the whole
              remedy — and it is per row, because the group's command covers
              a company and the unit that matters here is one filing. */}
          {!enabled && <div className="w-full"><Command>{it.command}</Command></div>}
        </li>
      ))}
    </ul>
  )
}

/**
 * The failing-check rows, which drill in.
 *
 * A reason is a *summary* row -- 108 pages behind one sentence -- so the
 * useful thing is opening it, not linking it. The pages come from a separate
 * request because 3,628 of them would otherwise be on the critical path of
 * opening this screen for every company.
 */
function ReasonRows({ symbol, items, onRun, enabled }: {
  symbol: string; items: GapItem[]
  onRun: (s: ActionSpec, c: string) => void; enabled: boolean
}) {
  const { t, v } = useT()
  const [open, setOpen] = useState<string | null>(null)

  if (items.length === 0) return null

  return (
    <ul className="mt-2 space-y-px">
      {items.map((it) => {
        const isOpen = open === it.label
        const name = it.checkName
          ? (v('checkName', it.checkName) ?? v('reviewReason', it.checkName))
          : null
        return (
          <li key={it.id} className="rounded-[var(--radius-control)] px-1.5 py-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {it.severity && (
                <Badge state={it.severity === 'error' ? 'fail' : 'warn'}>{it.severity}</Badge>
              )}
              <span className="text-[length:var(--text-data)] text-[var(--text)]">
                {name ?? <Untranslated>{it.checkName ?? it.label}</Untranslated>}
              </span>
              {it.detail && (
                <span className="tnum text-[length:var(--text-micro)] text-[var(--text-muted)]">
                  {it.detail}
                </span>
              )}
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : it.label)}
                className="link-inline text-[length:var(--text-micro)] text-[var(--accent)]"
              >
                {isOpen ? t('gaps.hidePages') : t('gaps.showPages')}
              </button>
            </div>
            {isOpen && <Pages symbol={symbol} reason={it.label} onRun={onRun} enabled={enabled} />}
          </li>
        )
      })}
    </ul>
  )
}

function Pages({ symbol, reason, onRun, enabled }: {
  symbol: string; reason: string
  onRun: (s: ActionSpec, c: string) => void; enabled: boolean
}) {
  const { t } = useT()
  const { data, error } = useQuery({
    queryKey: keys.gapPages(symbol, reason),
    queryFn: () =>
      api<{ pages: FailingPage[] }>(
        `/api/gaps/${encodeURIComponent(symbol)}/pages?reason=${encodeURIComponent(reason)}`,
      ),
  })

  if (error) return <div className="mt-1 text-[length:var(--text-micro)] text-[var(--fail)]">{(error as Error).message}</div>
  if (!data) return <Skeleton className="mt-1.5 h-16 w-full" />
  if (data.pages.length === 0) return null

  return (
    <ul className="mt-1.5 space-y-px border-s border-[var(--border)] ps-2.5">
      {data.pages.map((p, i) => (
        <li
          key={`${p.documentId}:${p.pageNo}:${i}`}
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-0.5"
        >
          <Link
            href={p.pageNo === null ? `/d/${p.documentId}` : `/d/${p.documentId}?p=${p.pageNo}`}
            className="link-inline tnum text-[length:var(--text-micro)] text-[var(--accent)]"
          >
            {p.label}
          </Link>
          {/*
            expected/got/off-by come out of validations.detail as the pipeline
            stored them. Never through Intl: a figure that has been reformatted
            cannot be compared against the page it was read from (§11).
          */}
          {p.detail && (
            <span className="tnum text-[length:var(--text-micro)] text-[var(--text-muted)]">
              <Untranslated>{p.detail}</Untranslated>
            </span>
          )}
          <span className="flex-1" />
          <Remedy
            spec={toActionSpec(symbol, p.action)}
            command={p.command}
            onRun={onRun}
            label={t('action.reread')}
            enabled={enabled}
          />
          {!enabled && <div className="w-full"><Command>{p.command}</Command></div>}
        </li>
      ))}
    </ul>
  )
}
